import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Chip, Tabs, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DataTableContainer, RejectReasonModal, ApproveConfirmModal, ViewGuard } from '../index';
import { profileLeavesTabStyles } from '../../pages/ProfilePage/tabs/ProfileLeavesTab.styles';
import PageContainer from '../PageContainer/PageContainer';
import { usePermissionsContext } from '../../providers/PermissionsProvider';
import { useAuth } from '../../hooks';
import { buildHasuraWhere } from '../../utils/buildHasuraWhere';

const WorkflowPage = ({
  entityType,
  screen,
  data = [],
  loading = false,
  columns = [],
  formFields = [],
  filterConfigs = [],
  searchConfig = {},
  formConfig = {},
  statusCounts = {},
  onFormSubmit,
  onApprove,
  onReject,
  onRowAction,
  rowActions = [],
  onDelete,
  transformEditData,
  canAdd = true,
  addButtonText,
  addButtonIcon,
  currentPage,
  showViewGuard = true,
  hideStatusTabs = false,
  exportButton,
  onExport,
  exportDisabled = false,
  exportLabel,
  getRowSx,
  // Backend pagination — forwarded to DataTableContainer. When
  // serverPagination is false/undefined, DataTableContainer falls back to
  // its existing client-side pagination logic.
  serverPagination = false,
  serverPage,
  serverPageSize,
  serverTotalCount,
  serverHasNextPage,
  onServerPageChange,
  onServerPageSizeChange,
  // Backend filter/search hookup. Pages that use server pagination should
  // pass the `setExtraWhereCondition` returned by their entity hook so the
  // table's filter/search UI can drive the Hasura where clause.
  setExtraWhereCondition,
  // Page-specific buttons rendered in the DataTable header (right side,
  // left of the standard "Add" button). Forwarded verbatim.
  extraHeaderActions = [],
  // DataTableContainer'a aynen iletilir (ustte de sayfalama).
  topPagination = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getScreenPermissions } = usePermissionsContext();
  const screenPermissions = screen ? getScreenPermissions(screen) : {};
  const isRootUser = user?.role === 'ROOT';
  const showExportButton = exportButton !== undefined ? exportButton : (screenPermissions.canDownload || false);
  const [status, setStatus] = useState('all');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [selectedApproveItem, setSelectedApproveItem] = useState(null);
  const [isApproving, setIsApproving] = useState(false);

  const tabDefs = [
    { key: 'all', label: t(`${entityType}.tabs.all`), count: statusCounts.all || 0 },
    { key: 'PENDING', label: t(`${entityType}.status.pending`), count: statusCounts.PENDING || 0 },
    { key: 'APPROVED', label: t(`${entityType}.status.approved`), count: statusCounts.APPROVED || 0 },
    { key: 'REJECTED', label: t(`${entityType}.status.rejected`), count: statusCounts.REJECTED || 0 },
  ];

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (status === 'all') return data;
    return data.filter(item => item.status === status);
  }, [data, status]);

  const handleRowAction = (actionType, row) => {
    if (onRowAction) {
      const result = onRowAction(actionType, row);
      if (result === true) return;
    }

    if (actionType === 'approve') {
      setSelectedApproveItem(row);
      setApproveModalOpen(true);
      return true;
    }
    if (actionType === 'reject') {
      setSelectedItem(row);
      setRejectModalOpen(true);
      return true;
    }
    return false;
  };

  const handleRejectConfirm = async (reason) => {
    if (!selectedItem || !onReject) return;
    setIsRejecting(true);
    try {
      await onReject(selectedItem.id, reason);
      setRejectModalOpen(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Reject error:', error);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleRejectCancel = () => {
    setRejectModalOpen(false);
    setSelectedItem(null);
  };

  const handleApproveConfirm = async () => {
    if (!selectedApproveItem || !onApprove) return;
    setIsApproving(true);
    try {
      await onApprove(selectedApproveItem.id);
      setApproveModalOpen(false);
      setSelectedApproveItem(null);
    } catch (error) {
      console.error('Approve error:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleApproveCancel = () => {
    setApproveModalOpen(false);
    setSelectedApproveItem(null);
  };

  // showIfRoot bayrakli kolonlar/filtreler yalnizca ROOT'a gosterilir —
  // DynamicCrudPage ile ayni davranis. Sistem geneli mimari kural.
  const pageConfig = useMemo(() => ({
    columns: (columns || []).filter((c) => (c.showIfRoot ? isRootUser : true)),
    filterConfigs: (filterConfigs || []).filter((f) => (f.showIfRoot ? isRootUser : true)),
    searchConfig,
    formConfig,
  }), [columns, filterConfigs, searchConfig, formConfig, isRootUser]);

  // Translate DataTable filter/search UI changes into a Hasura `_bool_exp`
  // and push it into the entity hook. Same shape as DynamicCrudPage.
  const handleFiltersChange = useCallback(
    (uiFilters, uiSearch) => {
      if (!setExtraWhereCondition) return;
      const where = buildHasuraWhere({
        filters: uiFilters,
        searchValue: uiSearch,
        searchFields: searchConfig?.fields || [],
        filterConfigs: filterConfigs || [],
      });
      setExtraWhereCondition(
        Object.keys(where).length > 0 ? where : null
      );
    },
    [setExtraWhereCondition, searchConfig?.fields, filterConfigs]
  );

  // Clear stale extra-where when server pagination is off, mirroring
  // DynamicCrudPage so toggling between modes never leaks a where clause.
  useEffect(() => {
    if (!serverPagination && setExtraWhereCondition) {
      setExtraWhereCondition(null);
    }
  }, [serverPagination, setExtraWhereCondition]);

  const content = (
    <PageContainer currentPage={currentPage}>
      <Box>
        {/* Status Tabs — onay akışı olmayan sayfalarda hideStatusTabs ile gizlenir */}
        {!hideStatusTabs && (
          <Box sx={profileLeavesTabStyles.tabBarContainer} style={{ marginLeft: 10, marginTop: 10, marginRight: 10 }}>
            <Tabs
              value={tabDefs.findIndex(tab => tab.key === status)}
              onChange={(_, idx) => setStatus(tabDefs[idx].key)}
              sx={profileLeavesTabStyles.tabsBar}
              TabIndicatorProps={{ sx: profileLeavesTabStyles.tabIndicator }}
            >
              {tabDefs.map(tab => (
                <Tab
                  key={tab.key}
                  label={
                    <Box sx={profileLeavesTabStyles.tabLabel(tab.key === status)}>
                      {tab.label}{' '}
                      <Box
                        component="span"
                        sx={profileLeavesTabStyles.tabCount(tab.key === status)}
                      >
                        {tab.count}
                      </Box>
                    </Box>
                  }
                  sx={profileLeavesTabStyles.tab(tab.key === status)}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Data Table */}
        <DataTableContainer
          columns={pageConfig.columns}
          data={filteredData}
          showSearch
          showFilters={pageConfig.filterConfigs.length > 0}
          showAddButton={canAdd}
          filterConfigs={pageConfig.filterConfigs}
          addButtonText={addButtonText}
          addButtonIcon={addButtonIcon}
          formFields={pageConfig.formConfig.fields}
          addModalTitle={pageConfig.formConfig.title?.add}
          loading={loading}
          onFormSubmit={onFormSubmit}
          searchFields={pageConfig.searchConfig.fields}
          searchPlaceholder={pageConfig.searchConfig.placeholder}
          rowActions={rowActions}
          onRowAction={handleRowAction}
          onDelete={onDelete}
          transformEditData={transformEditData}
          exportButton={showExportButton}
          onExport={onExport}
          exportDisabled={exportDisabled}
          exportLabel={exportLabel}
          getRowSx={getRowSx}
          rowsPerPage={serverPagination ? serverPageSize : undefined}
          serverPagination={serverPagination}
          serverPage={serverPage}
          serverPageSize={serverPageSize}
          serverTotalCount={serverTotalCount}
          serverHasNextPage={serverHasNextPage}
          onServerPageChange={onServerPageChange}
          onServerPageSizeChange={onServerPageSizeChange}
          serverFilters={!!serverPagination && !!setExtraWhereCondition}
          onFiltersChange={handleFiltersChange}
          extraHeaderActions={extraHeaderActions}
          topPagination={topPagination}
        />

        {/* Reject Reason Modal */}
        <RejectReasonModal
          open={rejectModalOpen}
          onClose={handleRejectCancel}
          onConfirm={handleRejectConfirm}
          itemName={selectedItem ? `${selectedItem.user?.first_name} ${selectedItem.user?.last_name}` : ''}
          loading={isRejecting}
          entityType={entityType}
        />

        {/* Approve Confirm Modal */}
        <ApproveConfirmModal
          open={approveModalOpen}
          onClose={handleApproveCancel}
          title={t(`${entityType}.actions.approve`)}
          loading={isApproving}
          onConfirm={handleApproveConfirm}
          item={selectedApproveItem}
          confirmText={t(`${entityType}.actions.approve`)}
          cancelText={t('modals.delete.cancel')}
          entityType={entityType}
        />
      </Box>
    </PageContainer>
  );

  if (showViewGuard && screen) {
    return <ViewGuard screen={screen}>{content}</ViewGuard>;
  }

  return content;
};

export default WorkflowPage; 